import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import SearchBar from "../components/SearchBar";
import Modal from "react-modal";
// JSONPretty removed in favor of a custom dark pre block for better readability
import LoadingModal from "../components/LoadingModal";
import UnauthorizedComponent from "../components/UnauthorizedComponent";
import Button from "../components/Button";
import InputText1 from "../components/InputText1";
import { adminServices } from "../services/AdminServices";
import { logExport } from "../services/ExportLogService";

const LogReports = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);

  const [loading, setLoading] = useState(false);

  const [viewData, setViewData] = useState(false);
  const [eventData, setEventData] = useState("");
  const [isJson, setIsJson] = useState(false);

  const tryParseJsonLoose = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return { ok: false };

    const candidates = [];
    candidates.push(raw);
    // Common case: JSON object/array stored with escaped quotes, e.g. {\"a\":1}
    candidates.push(raw.replace(/\\"/g, '"').replace(/\\\\/g, '\\'));
    // Sometimes the JSON is wrapped in quotes
    if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
      candidates.push(raw.slice(1, -1));
    }

    for (const c of candidates) {
      try {
        const parsed = JSON.parse(c);
        if (typeof parsed === 'string') {
          const inner = parsed.trim();
          if ((inner.startsWith('{') && inner.endsWith('}')) || (inner.startsWith('[') && inner.endsWith(']'))) {
            try {
              return { ok: true, value: JSON.parse(inner) };
            } catch {
              // fallthrough
            }
          }
        }
        return { ok: true, value: parsed };
      } catch {
        // continue
      }
    }

    return { ok: false };
  }

  const parseFromToDescription = (description) => {
    let text = String(description ?? '').trim();
    if (!text) return null;

    // Example: "json string {FROM: N/A | TO: {\"RecordId\":0,...}}"
    text = text.replace(/^json\s*string\s*/i, '').trim();

    const fromIdx = text.toUpperCase().indexOf('FROM:');
    const toIdx = text.toUpperCase().indexOf('TO:');
    if (fromIdx === -1 || toIdx === -1 || toIdx < fromIdx) return null;

    const pipeIdx = text.indexOf('|', fromIdx);
    if (pipeIdx === -1 || pipeIdx > toIdx) return null;

    const fromPart = text.slice(fromIdx + 5, pipeIdx).trim();
    let toPart = text.slice(toIdx + 3).trim();
    // Strip enclosing braces if present around the whole payload
    if (toPart.endsWith('}')) {
      // keep as-is; it might be JSON
    }
    // Remove trailing outer brace if the whole string was like {FROM: ... | TO: ...}
    if (toPart.endsWith('}')) {
      // ok
    }

    const extractBalancedJsonString = (input) => {
      const s = String(input ?? '').trim();
      const startBrace = s.indexOf('{');
      const startBracket = s.indexOf('[');
      let start = -1;
      let openChar = '';
      let closeChar = '';

      if (startBrace === -1 && startBracket === -1) return null;
      if (startBracket !== -1 && (startBrace === -1 || startBracket < startBrace)) {
        start = startBracket;
        openChar = '[';
        closeChar = ']';
      } else {
        start = startBrace;
        openChar = '{';
        closeChar = '}';
      }

      let depth = 0;
      let inString = false;
      let escaped = false;
      for (let i = start; i < s.length; i++) {
        const ch = s[i];
        if (escaped) {
          escaped = false;
          continue;
        }
        if (ch === '\\') {
          escaped = true;
          continue;
        }
        if (ch === '"') {
          inString = !inString;
          continue;
        }
        if (inString) continue;

        if (ch === openChar) depth++;
        if (ch === closeChar) depth--;
        if (depth === 0) {
          return s.slice(start, i + 1);
        }
      }
      return null;
    }

    const parseFragment = (fragment) => {
      const f = String(fragment ?? '').trim();
      if (!f) return null;
      if (/^(n\/a|null|none)$/i.test(f)) return f.toUpperCase() === 'N/A' ? 'N/A' : null;

      const extracted = extractBalancedJsonString(f);
      if (extracted) {
        const parsedExtracted = tryParseJsonLoose(extracted);
        if (parsedExtracted.ok) return parsedExtracted.value;
      }

      const parsed = tryParseJsonLoose(f);
      return parsed.ok ? parsed.value : f;
    }

    return {
      FROM: parseFragment(fromPart),
      TO: parseFragment(toPart),
    };
  }

  const formatDescriptionForModal = (description) => {
    const raw = String(description ?? '');

    // 1) Strict JSON
    const direct = tryParseJsonLoose(raw);
    if (direct.ok) return { isJson: true, value: direct.value };

    // 2) FROM/TO format with embedded JSON fragments
    const fromTo = parseFromToDescription(raw);
    if (fromTo) return { isJson: true, value: fromTo };

    // 3) Fallback: plain text
    return { isJson: false, value: raw };
  }

  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const limit = 100;
  const [offset, setOffset] = useState(0);

  const modalStyles = {
    overlay: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
    content: {
      width: "80%",
      margin: "auto",
      height: "70vh",
      background: '#0b1220',
      color: '#e6eef8',
      borderRadius: '8px',
      padding: '12px 16px',
    },
  };

  const tableStyles = {
    table: { style: { tableLayout: 'fixed' } },
    headCells: { style: { fontWeight: '600', fontSize: '0.85rem' } },
    cells: { style: { wordBreak: 'break-word', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    if (offset > 0) loadLogs();
  }, [offset]);

  const loadLogs = async () => {
    setLoading(true);

    try {
      const response = await adminServices.storeProcedure2("sp_get_logs_report", {
        p_date_from: dateFrom || '',
        p_date_to: dateTo || '',
        p_search: '',
        p_offset: offset,
        p_limit: limit
      });
      const logs = response.data;

      setData(logs);
      setFilteredData(logs);

      buildColumns();
      setLoading(false);
    } catch (e) {
      if (e.status === 401) window.location.href = "/";
    }
  };

  const buildColumns = () => {
    const badgeColor = (type) => {
      if (!type) return '#6c757d';
      const t = type.toString().toLowerCase();
      if (t.includes('system')) return '#0d6efd';
      if (t.includes('transaction')) return '#6f42c1';
      if (t.includes('download') || t.includes('export')) return '#198754';
      if (t.includes('edit') || t.includes('update')) return '#fd7e14';
      if (t.includes('error') || t.includes('fail')) return '#dc3545';
      return '#6c757d';
    }

    const renderActionType = (row) => {
      const color = badgeColor(row.action_type);
      const style = { backgroundColor: color, color: '#fff', padding: '4px 8px', borderRadius: 12, fontSize: 12, display: 'inline-block' };
      return <span style={style}>{row.action_type}</span>;
    }

    const cols = [
      { name: "ID", selector: (row) => row.id, sortable: true, width: "80px" },
      {
        name: "Timestamp",
        selector: (row) =>
          new Date(row.timestamp).toLocaleString(),
        sortable: true,
        width: "200px",
      },
      { name: "User", selector: (row) => row.user, sortable: true },
      { name: "Platform", selector: (row) => row.platform, sortable: true },
      { name: "Module", selector: (row) => row.module, sortable: true },
      { name: "Action", selector: (row) => row.action, sortable: true },
      {
        name: "Action Type",
        selector: (row) => row.action_type,
        sortable: true,
        cell: renderActionType,
      },
      {
        name: "Description",
        selector: (row) => {
          const d = row.description || "";
          const max = 120;
          const truncated = d.length > max ? d.substring(0, max) + "..." : d;
          return (
            <span
              title={d}
              style={{
                display: "inline-block",
                maxWidth: "700px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {truncated}
            </span>
          );
        },
        grow: 2,
      },
    ];

    setColumns(cols);
  };

  const exportToCSV = async (rows) => {
    // Log the export
    await logExport('Log Reports');

    const mapped = rows.map((row) => ({
      ID: row.id,
      Timestamp: new Date(row.timestamp).toLocaleString(),
      User: row.user,
      Platform: row.platform,
      Module: row.module,
      Action: row.action,
      ActionType: row.action_type,
      Description: row.description,
    }));

    const headers = Object.keys(mapped[0]).join(",");
    const csvRows = mapped.map((r) => Object.values(r).join(","));
    const csvContent = [headers, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "LogReport.csv");
    link.click();
  };

  const getJsonText = () => {
    if (isJson) {
      try {
        return typeof eventData === 'string' ? eventData : JSON.stringify(eventData, null, 2);
      } catch (e) {
        return String(eventData || '');
      }
    }
    return String(eventData || '');
  }

  const escapeHtml = (str) => {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  const jsonToHtml = (data, indent = 0) => {
    const indentPx = 14;
    const spacer = (n) => `<span style="display:inline-block;width:${n * indentPx}px"></span>`;

    const keyHtml = (k) => `<span style="color:#ffd6a5;font-weight:600">\"${escapeHtml(k)}\"</span>`;
    const colonHtml = () => `<span style="color:#94a3b8;padding:0 6px">:</span>`;
    const strHtml = (s) => `<span style="color:#a8dadc">\"${escapeHtml(s)}\"</span>`;
    const numHtml = (n) => `<span style="color:#ffd166">${n}</span>`;
    const boolHtml = (b) => `<span style="color:#06d6a0">${b}</span>`;
    const nullHtml = () => `<span style="color:#ff6b6b">null</span>`;

    if (data === null) return nullHtml();
    if (typeof data === 'number') return numHtml(data);
    if (typeof data === 'boolean') return boolHtml(data);
    if (typeof data === 'string') return strHtml(data);
    if (Array.isArray(data)) {
      if (data.length === 0) return '[]';
      let out = `[<br>`;
      for (let i = 0; i < data.length; i++) {
        out += spacer(indent + 1) + jsonToHtml(data[i], indent + 1) + (i < data.length - 1 ? ',' : '') + '<br>';
      }
      out += spacer(indent) + `]`;
      return out;
    }
    // object
    const keys = Object.keys(data);
    if (keys.length === 0) return '{}';
    let out = `{<br>`;
    keys.forEach((k, idx) => {
      const value = data[k];
      out += spacer(indent + 1) + keyHtml(k) + colonHtml() + jsonToHtml(value, indent + 1) + (idx < keys.length - 1 ? ',' : '') + '<br>';
    });
    out += spacer(indent) + `}`;
    return out;
  }

  // Collapsible React JSON viewer component
  const JsonViewer = ({ data, level = 0 }) => {
    const [open, setOpen] = useState(level < 1); // top-level expanded
    const containerStyle = { fontFamily: 'monospace', fontSize: 13, color: '#cfeefb' };
    const keyStyle = { color: '#ffd6a5', fontWeight: 600 };
    const colonStyle = { color: '#94a3b8', padding: '0 6px' };
    const stringStyle = { color: '#a8dadc' };
    const numberStyle = { color: '#ffd166' };
    const boolStyle = { color: '#06d6a0' };
    const nullStyle = { color: '#ff6b6b' };

    const renderValue = (v, idx) => {
      if (v === null) return <span style={nullStyle}>null</span>;
      if (Array.isArray(v)) return <JsonViewer key={idx} data={v} level={level + 1} />;
      if (typeof v === 'object') return <JsonViewer key={idx} data={v} level={level + 1} />;
      if (typeof v === 'string') return <span style={stringStyle}>"{v}"</span>;
      if (typeof v === 'number') return <span style={numberStyle}>{v}</span>;
      if (typeof v === 'boolean') return <span style={boolStyle}>{String(v)}</span>;
      return <span>{String(v)}</span>;
    }

    if (data === null || typeof data !== 'object') {
      // primitive
      if (typeof data === 'string') return <span style={stringStyle}>"{data}"</span>;
      if (typeof data === 'number') return <span style={numberStyle}>{data}</span>;
      if (typeof data === 'boolean') return <span style={boolStyle}>{String(data)}</span>;
      return <span style={nullStyle}>null</span>;
    }

    // object or array
    const isArray = Array.isArray(data);
    const entries = isArray ? data : Object.entries(data);

    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setOpen(!open)} style={{ background: 'transparent', border: 'none', color: '#9fb1c8', cursor: 'pointer' }}>
            {open ? '▾' : '▸'}
          </button>
          <span style={{ color: '#94a3b8' }}>{isArray ? `[Array(${data.length})]` : '{ Object }'}</span>
        </div>
        {open && (
          <div style={{ marginLeft: 18, marginTop: 6 }}>
            {isArray
              ? data.map((item, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <span style={{ color: '#94a3b8', marginRight: 6 }}>{i}:</span>
                    {renderValue(item, i)}
                  </div>
                ))
              : Object.keys(data).map((k, idx) => (
                  <div key={String(k) + idx} style={{ marginBottom: 6 }}>
                    <span style={keyStyle}>"{k}"</span>
                    <span style={colonStyle}> : </span>
                    {renderValue(data[k], idx)}
                  </div>
                ))}
          </div>
        )}
      </div>
    );
  }

  const isPlainObject = (v) => {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
  }

  const hasObjectData = (v) => {
    if (!isPlainObject(v)) return false;
    return Object.keys(v).length > 0;
  }

  const hasFromToShape = (v) => {
    if (!isPlainObject(v)) return false;
    return Object.prototype.hasOwnProperty.call(v, 'FROM') || Object.prototype.hasOwnProperty.call(v, 'TO');
  }

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^(n\/a|null|none)$/i.test(trimmed)) return '';
      // Pretty-print ISO timestamps
      const maybeDate = new Date(trimmed);
      if (!Number.isNaN(maybeDate.getTime()) && /\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
        return maybeDate.toLocaleString();
      }
      return trimmed;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  const KeyValueTable = ({ title, obj }) => {
    if (!hasObjectData(obj)) return null;

    const rows = Object.entries(obj);
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 700, margin: '6px 0 8px', color: '#e6eef8' }}>{title}</div>
        <div style={{ border: '1px solid rgba(148,163,184,0.25)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#0b1a2a' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#94a3b8', width: '28%', borderBottom: '1px solid rgba(148,163,184,0.25)' }}>Key</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#94a3b8', borderBottom: '1px solid rgba(148,163,184,0.25)' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([k, v], idx) => {
                const formatted = formatCellValue(v);
                if (!formatted) return null;
                const isMultiLine = formatted.includes('\n');
                return (
                  <tr key={k + idx} style={{ background: idx % 2 === 0 ? '#07101a' : '#06101c' }}>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', color: '#ffd6a5', fontWeight: 600, borderBottom: '1px solid rgba(148,163,184,0.10)' }}>
                      {k}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', color: '#cfeefb', borderBottom: '1px solid rgba(148,163,184,0.10)' }}>
                      {isMultiLine ? (
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', color: 'inherit' }}>{formatted}</pre>
                      ) : (
                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{formatted}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (localStorage.getItem("currentRole") != 1) {
    return <UnauthorizedComponent />;
  }

  return (
    <div className="container">
      <LoadingModal show={loading} message="Loading Logs Report..." />

      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h2 style={{margin:0}}>Logs Report</h2>
        <div>
          <Button variant="success" size="sm" onClick={() => exportToCSV(filteredData)}>
            <i className="fa fa-download" style={{marginRight:6}}></i> Export to CSV
          </Button>
        </div>
      </div>

      {/* MODAL */}
      <Modal
        style={modalStyles}
        isOpen={viewData}
        onRequestClose={() => {
          setViewData(false);
          setEventData("");
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, color: '#e6eef8' }}>Log Details</h3>
          <div>
            <button
              style={{
                backgroundColor: "transparent",
                borderColor: "transparent",
                color: "#cbd5e1",
                borderRadius: 100,
                fontSize: 20,
                fontWeight: "bolder",
                fontFamily: "monospace",
                borderWidth: 0,
                cursor: 'pointer'
              }}
              onClick={() => {
                setViewData(false);
                setEventData("");
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          {isJson ? (
            <div style={{
              background: '#07101a',
              color: '#cfeefb',
              padding: 12,
              borderRadius: 6,
              maxHeight: '60vh',
              overflow: 'auto',
              fontSize: 13,
              lineHeight: '1.5',
            }}>
              {hasFromToShape(eventData) && (hasObjectData(eventData?.FROM) || hasObjectData(eventData?.TO)) ? (
                <div>
                  <KeyValueTable title="FROM" obj={eventData?.FROM} />
                  <KeyValueTable title="TO" obj={eventData?.TO} />
                </div>
              ) : (
                <JsonViewer data={eventData} />
              )}
            </div>
          ) : (
            <div style={{ background: '#07101a', color: '#e6eef8', padding: 12, borderRadius: 6, maxHeight: '60vh', overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: escapeHtml(eventData) }} />
          )}
        </div>
      </Modal>

      {/* FILTERS */}
      <div className="d-flex justify-content-start align-items-end" style={{gap:12, marginTop:12}}>
        <InputText1 title="Date From" type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} />
        <InputText1 title="Date To" type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} />
        <div style={{ marginBottom: 10 }}>
          <Button variant="primary" onClick={() => { setOffset(0); loadLogs(); }}>Search</Button>
        </div>
      </div>

      {/* SEARCH FIELD */}
      <div style={{ marginTop: 12 }}>
        <SearchBar
          placeholder="Search by ID, User, Action, Platform, Module, Action Type, or Description (includes FROM and TO data)"
          onChange={(e) => {
            const v = e.target.value.toLowerCase();
            const filtered = data.filter((item) =>
              Object.keys(item).some(
                (k) =>
                  item[k] &&
                  item[k].toString().toLowerCase().includes(v.toLowerCase())
              )
            );
            setFilteredData(filtered);
          }}
        />

        <div style={{ float: 'right', marginTop: 0  }}>
          <Button variant="light" size="xs" onClick={() => { if (offset >= 0) { setOffset((prev) => Math.max(prev - limit, 0)); loadLogs(); } }}>
            <i className="fa fa-arrow-left"></i>
          </Button>
          &nbsp;
          <Button variant="light" size="xs" onClick={() => setOffset((prev) => prev + limit)}>
            <i className="fa fa-arrow-right"></i>
          </Button>
        </div>
      </div>

      <div style={{ height: "25px" }}></div>

      {/* TABLE */}
      <div style={{ background: '#fff', borderRadius: 8, padding: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <DataTable
          columns={columns}
          data={filteredData}
          customStyles={tableStyles}
          highlightOnHover
          pointerOnHover
          striped
          onRowClicked={(row) => {
            setViewData(true);

            const formatted = formatDescriptionForModal(row.description);
            setEventData(formatted.value);
            setIsJson(formatted.isJson);
          }}
          noHeader
        />
      </div>
    </div>
  );
};

export default LogReports;
